import { z } from 'zod';

/**
 * Runtime validation schemas for Action Plan API responses
 * Ensures type safety at runtime and catches API contract violations
 */

export const ActionPlanItemSchema = z.object({
  statement: z.string().optional(),
});

export const PairedItemSchema = z.object({
  sortOrder: z.number(),
  commitment: ActionPlanItemSchema.optional(),
  expectation: ActionPlanItemSchema.optional(),
});

export const SummaryParagraphSchema = z.object({
  title: z.string().optional(),
  content: z.string(),
});

export const SummarySchema = z.object({
  paragraphs: z.array(SummaryParagraphSchema),
});

export const RecommendationSchema = z.object({
  number: z.number(),
  title: z.string(),
  content: z.array(z.string()),
  example: z.string().optional(),
});

export const ActionRecommendationsSchema = z.object({
  recommendations: z.array(RecommendationSchema),
});

export const BaselineComparisonItemSchema = z.object({
  title: z.string(),
  currentScore: z.number(),
  previousScore: z.number(),
  scoreChange: z.number(),
  changeDirection: z.enum(['up', 'down', 'neutral']),
  interpretation: z.string(),
});

export const BaselineComparisonSchema = z.object({
  categoryName: z.string(),
  currentCategoryName: z.string(),
  items: z.array(BaselineComparisonItemSchema),
});

const SegmentBreakdownSchema = z.object({
  segment: z.string(),
  count: z.number(),
});

const BaselineDataSchema = z.object({
  previousScore: z.number(),
  currentScore: z.number(),
  scoreChange: z.number(),
  scoreTrend: z.enum(['up', 'down', 'stable']),
});

export const ActionPlanAggregatedSchema = z.object({
  surveyId: z.number(),
  categoryId: z.number(),
  categoryName: z.string(),
  participantCount: z.number().min(0),
  teamMemberCount: z.number().min(1),
  pairedItems: z.array(PairedItemSchema),
  summary: SummarySchema.optional(),
  actionRecommendations: ActionRecommendationsSchema.optional(),
  baselineComparison: BaselineComparisonSchema.optional(),
  createdAt: z.string().optional(),
  nextSurveyDate: z.string().optional(),
  baseline: BaselineDataSchema.optional(),
  tenureSegments: z.array(SegmentBreakdownSchema).optional(),
  roleSegments: z.array(SegmentBreakdownSchema).optional(),
});

export const FocusReasonSchema = z.enum(['lowScoreAndDisagreement', 'lowScore', 'disagreement', 'combination']);

export const FocusCategorySchema = z.object({
  categoryId: z.number(),
  categoryName: z.string(),
  score: z.number(),
  standardDeviation: z.number().optional(),
  reason: z.string(),
  reasonKey: FocusReasonSchema.optional(),
  nextSurveyDate: z.string().nullable(),
});

export const ActionPlanDtoSchema = z.object({
  id: z.string(),
  surveyId: z.number(),
  nextSurveyId: z.number().optional(),
  categoryId: z.number(),
  commitments: z.array(z.string()).optional(),
  expectations: z.array(z.string()).optional(),
  createdAt: z.string(),
  tenantId: z.number().optional(),
});

// Type exports for use in components
export type ActionPlanAggregated = z.infer<typeof ActionPlanAggregatedSchema>;
export type FocusCategory = z.infer<typeof FocusCategorySchema>;
export type FocusReason = z.infer<typeof FocusReasonSchema>;
export type ActionPlanDto = z.infer<typeof ActionPlanDtoSchema>;
